import React, { useEffect, useState } from "react";
import greater from "../../images/greater.svg";
import { Calendar1Icon, Filter } from "lucide-react";
import { useGetTransactionsMutation } from "../../store/apis";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
const TRANSACTION_TYPES = {
  ALL: "all",
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw",
  BET: "bet",
  TAX: "tax",
};
const Transaction = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedType, setSelectedType] = useState(TRANSACTION_TYPES.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [paginatedTransactions, setPaginatedTransactions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 8;
  const user = useSelector(selectUser);

  // Use mutation to fetch transactions
  const [getTransactions] = useGetTransactionsMutation();

  // Fetch all transactions
  const fetchAllTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await getTransactions({
        customer_id: user.id,
      }).unwrap();

      if (response && response.rows) {
        const mappedData = response.rows.map((item) => ({
          referenceId: item.transaction_id.slice(0, 10),
          dateTime: item.created_date,
          description: getDescriptionFromType(
            item.transaction_type,
            item.reference_transaction_id
          ),
          amount:
            item.transaction_type === "deposit"
              ? `+${item.amount}`
              : `-${item.amount}`,
          status: item.status,
          balance: "N/A",
          type: item.transaction_type,
          rawDate: new Date(item.created_date),
        }));
        console.log("mapped data", mappedData);
        setAllTransactions(mappedData);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate description based on transaction type
  function getDescriptionFromType(type, reference) {
    switch (type) {
      case "deposit":
        return `Deposit via Bank Transfer (Ref: ${reference})`;
      case "withdraw":
        return `Withdrawal Request (Ref: ${reference})`;
      case "bet":
        return `Sports Bet Placement (Ref: ${reference})`;
      case "tax":
        return `Excise Tax for bet id: ${reference}`;
      default:
        return `Transaction (Ref: ${reference})`;
    }
  }

  // Initial fetch
  useEffect(() => {
    fetchAllTransactions();
  }, []);

  // Apply filters
  useEffect(() => {
    if (allTransactions.length === 0) return;

    let filtered = [...allTransactions];

    // Apply transaction type filter
    if (selectedType !== TRANSACTION_TYPES.ALL) {
      filtered = filtered.filter((item) => item.type === selectedType);
    }

    // Apply date from filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((item) => item.rawDate >= fromDate);
    }

    // Apply date to filter
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((item) => item.rawDate <= toDate);
    }

    // Apply active/history tab filter
    if (activeTab === "active") {
      filtered = filtered.filter(
        (item) => item.status === "pending" || item.status === "processing"
      );
    } else {
      filtered = filtered.filter(
        (item) =>
          item.status === "successful" ||
          item.status === "failed" ||
          item.status === "cancelled"
      );
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [allTransactions, selectedType, dateFrom, dateTo, activeTab]);

  // Apply pagination
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedTransactions(filteredTransactions.slice(startIndex, endIndex));
  }, [filteredTransactions, currentPage]);

  // Format date for display
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";

    const date = new Date(dateTime);
    return date
      .toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  // Handle page change
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get transaction type color
  const getTypeColor = (type) => {
    switch (type) {
      case "deposit":
        return "bg-emerald-100 text-emerald-800";
      case "withdraw":
        return "bg-amber-100 text-amber-800";
      case "bet":
        return "bg-blue-100 text-blue-800";
      case "tax":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Reset filters
  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSelectedType(TRANSACTION_TYPES.ALL);
  };

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    let startPage = Math.max(currentPage - 1, 2);
    let endPage = Math.min(currentPage + 1, totalPages - 1);

    if (currentPage <= 2) {
      endPage = 4;
    }
    if (currentPage >= totalPages - 1) {
      startPage = totalPages - 3;
    }

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="w-full pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
            <button
              className={`py-4 px-6 text-center ${
                activeTab === "active"
                  ? "border-b-2 border-yellow bg-navHover text-white"
                  : "text-white"
              } font-medium`}
              onClick={() => setActiveTab("active")}
            >
              ACTIVE
            </button>
            <button
              className={`py-4 px-6 text-center ${
                activeTab === "history"
                  ? "border-b-2 border-yellow bg-navHover text-white"
                  : "text-white"
              } font-medium`}
              onClick={() => setActiveTab("history")}
            >
              HISTORY
            </button>
          </div>

          {/* Filter Controls */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
              <div className="flex items-center mb-4 md:mb-0">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 bg-yellow border border-solid border-2 border-transparent hover:bg-transparent hover:border-yellow hover:text-yellow rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </button>
                {(dateFrom ||
                  dateTo ||
                  selectedType !== TRANSACTION_TYPES.ALL) && (
                  <button
                    onClick={resetFilters}
                    className="ml-2 px-4 py-2 bg-red-50 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">
                  {filteredTransactions.length} transactions found
                </span>
                <button
                  onClick={fetchAllTransactions}
                  className="ml-2 px-4 py-2 bg-yellow border border-solid border-2 border-transparent hover:bg-transparent hover:border-yellow hover:text-yellow rounded-md shadow-sm text-sm font-medium text-white"
                >
                  Refresh
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="bg-shadeGreen p-4 rounded-md shadow-md mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      From Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-yellow focus:border-white sm:text-sm bg-[#E8F0FE] text-black"
                      />
                      <Calendar1Icon className="h-5 w-5 text-gray-400 absolute left-3 top-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      To Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-yellow focus:border-white sm:text-sm bg-[#E8F0FE] text-black"
                      />
                      <Calendar1Icon className="h-5 w-5 text-gray-400 absolute left-3 top-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Transaction Type
                    </label>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="block w-full py-2 px-3 border border-yellow bg-[#0B1E21] rounded-md shadow-sm focus:outline-none focus:ring-yellow focus:border-yellow sm:text-sm text-white dark:text-white"
                    >
                      <option value={TRANSACTION_TYPES.ALL}>All Types</option>
                      <option value={TRANSACTION_TYPES.DEPOSIT}>Deposit</option>
                      <option value={TRANSACTION_TYPES.WITHDRAW}>
                        Withdraw
                      </option>
                      {/* <option value={TRANSACTION_TYPES.BET}>Bet</option>
                      <option value={TRANSACTION_TYPES.TAX}>Tax</option> */}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : paginatedTransactions.length > 0 ? (
              <div className="bg-shadeGreen shadow-md rounded-lg overflow-hidden">
                {/* Responsive Table for both Desktop and Mobile */}
                <div className="overflow-x-auto w-full">
                  <table className="min-w-full">
                    <thead className="bg-shadeGreen">
                      <tr className="border-b border-gray-500">
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedTransactions.map((transaction, index) => (
                        <tr key={index} className="">
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getTypeColor(
                                transaction.type
                              )}`}
                            >
                              {transaction.type.charAt(0).toUpperCase() +
                                transaction.type.slice(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {transaction.referenceId}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {formatDateTime(transaction.dateTime)}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm">
                            <span
                              className={
                                transaction.amount.startsWith("+")
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {transaction.amount}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                                transaction.status
                              )}`}
                            >
                              {transaction.status.charAt(0).toUpperCase() +
                                transaction.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-[#0B1E21] p-6 text-center rounded-lg shadow">
                <p className="text-gray-600 dark:text-white">
                  No transactions found matching your criteria.
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredTransactions.length > 0 && (
            <div className="flex items-center justify-between mt-6 flex-col sm:flex-row">
              <div className="mb-4 sm:mb-0">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredTransactions.length
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">
                    {filteredTransactions.length}
                  </span>{" "}
                  results
                </p>
              </div>
              {/* <nav className="flex flex-wrap justify-center">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${
                    currentPage === 1
                      ? "bg-yellow dark:bg-gray-700 text-white dark:text-gray-500"
                      : "bg-[#0B1E21] dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  } text-sm font-medium border-gray-300 dark:border-gray-600`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {getPaginationNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => page !== "..." && handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                      page === currentPage
                        ? "bg-yellow dark:bg-blue-900 text-blue-600 dark:text-blue-300"
                        : "bg-[#0B1E21] dark:bg-gray-800 text-white dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    } ${page === "..." ? "cursor-default" : ""}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={
                    currentPage ===
                    Math.ceil(filteredTransactions.length / itemsPerPage)
                  }
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${
                    currentPage ===
                    Math.ceil(filteredTransactions.length / itemsPerPage)
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                      : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  } text-sm font-medium border-gray-300 dark:border-gray-600`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transaction;
